import { useState, useMemo, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiBadge from "components/VuiBadge";
import { requestBankConsent, listClientProfiles } from "services/tenantUserAdminService";
import { useCompany } from "context/CompanyContext";
import { useAuthz } from "context/AuthzContext";
import { TC } from "../../tokens/text";
import {
  IoCloudUploadOutline,
  IoCheckmarkCircle,
  IoWarning,
  IoTimeOutline,
  IoBanOutline,
  IoRefreshOutline,
  IoEyeOutline,
  IoTrashOutline,
  IoLinkOutline,
  IoShieldCheckmarkOutline,
  IoBusinessOutline,
  IoPersonOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_CONNECTIONS = [
  {
    id: "conn-001",
    clientName: "Acme Ltd",
    clientEmail: "finance@acme.co.uk",
    bank: "Barclays",
    country: "UK",
    status: "active",
    consentId: "cns-aab112",
    lastRefresh: "2026-04-11T09:00:00Z",
    expiresAt: "2026-07-09T09:00:00Z",
    permissions: ["Account Details", "Balances", "Transactions"],
    grantedAt: "2026-04-10T14:23:00Z",
  },
  {
    id: "conn-002",
    clientName: "TechStart Solutions",
    clientEmail: "cfo@techstart.io",
    bank: "HSBC",
    country: "UK",
    status: "expiring",
    consentId: "cns-bbc334",
    lastRefresh: "2026-04-10T18:30:00Z",
    expiresAt: "2026-04-21T18:30:00Z",
    permissions: ["Account Details", "Balances", "Transactions"],
    grantedAt: "2026-01-21T18:30:00Z",
  },
  {
    id: "conn-003",
    clientName: "Green Energy Co",
    clientEmail: "accounts@greenenergy.eu",
    bank: "Monzo",
    country: "UK",
    status: "pending",
    consentId: null,
    lastRefresh: null,
    expiresAt: null,
    permissions: [],
    grantedAt: null,
  },
  {
    id: "conn-004",
    clientName: "Nova Retail Group",
    clientEmail: "nova@novaretail.co.uk",
    bank: "Lloyds",
    country: "UK",
    status: "revoked",
    consentId: "cns-dde556",
    lastRefresh: "2026-03-01T10:00:00Z",
    expiresAt: null,
    permissions: ["Account Details", "Balances", "Transactions"],
    grantedAt: "2025-12-01T10:00:00Z",
  },
  {
    id: "conn-005",
    clientName: "Maple Consulting",
    clientEmail: "billing@mapleconsult.com",
    bank: "NatWest",
    country: "UK",
    status: "active",
    consentId: "cns-eef778",
    lastRefresh: "2026-04-11T07:15:00Z",
    expiresAt: "2026-07-10T07:15:00Z",
    permissions: ["Account Details", "Balances", "Transactions"],
    grantedAt: "2026-04-11T07:15:00Z",
  },
];

const UK_BANKS = ["Barclays", "HSBC", "Lloyds", "NatWest", "Santander", "Monzo", "Starling", "TSB", "Metro Bank", "Other"];
const EU_BANKS = ["Deutsche Bank", "BNP Paribas", "ING", "Revolut", "N26", "Other"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  active:   { label: "Active",   color: "success", icon: <IoCheckmarkCircle />, hex: "#01b574" },
  expiring: { label: "Expiring", color: "warning", icon: <IoWarning />,         hex: "#f44336" },
  pending:  { label: "Pending",  color: "info",    icon: <IoTimeOutline />,      hex: "#0075ff" },
  revoked:  { label: "Revoked",  color: "error",   icon: <IoBanOutline />,       hex: "#f44336" },
  expired:  { label: "Expired",  color: "error",   icon: <IoTimeOutline />,      hex: "#777" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function daysUntil(iso) {
  if (!iso) return null;
  const diff = new Date(iso) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

const selectSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "rgba(255,255,255,0.05) !important",
    color: "white !important",
    borderRadius: "8px",
    border: "0.5px solid rgba(255,255,255,0.2)",
    minHeight: 40,
    fontSize: 13,
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSvgIcon-root": { color: "white !important" },
};
const menuProps = {
  PaperProps: {
    sx: {
      backgroundImage: "none",
      backgroundColor: "rgba(15,18,59,0.98)",
      color: TC.primary,
      border: "1px solid #d8e6f7",
      "& .MuiMenuItem-root": { color: TC.primary, fontSize: 13 },
      "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(0,117,255,0.15)" },
    },
  },
};

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon, color }) {
  return (
    <Card>
      <VuiBox p={2.5} display="flex" alignItems="center" gap={2}>
        <VuiBox
          display="flex" alignItems="center" justifyContent="center"
          width={44} height={44} borderRadius="12px"
          sx={{ background: `${color}22`, fontSize: 22, color }}
        >
          {icon}
        </VuiBox>
        <VuiBox>
          <VuiTypography variant="h4" fontWeight="bold" lineHeight={1} sx={{ color: TC.primary }}>{value}</VuiTypography>
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{label}</VuiTypography>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

// ─── Connection card ──────────────────────────────────────────────────────────
function ConnectionCard({ conn, onViewDetails, onRenew, onRevoke }) {
  const cfg = STATUS_CONFIG[conn.status] || STATUS_CONFIG.expired;
  const days = daysUntil(conn.expiresAt);

  return (
    <Card>
      <VuiBox p={2.5}>
        {/* Header row */}
        <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <VuiBox display="flex" gap={1.5} alignItems="flex-start">
            <VuiBox
              display="flex" alignItems="center" justifyContent="center"
              width={40} height={40} borderRadius="10px"
              sx={{ background: "rgba(0,117,255,0.12)", fontSize: 20, color: TC.linkHover }}
            >
              <IoBusinessOutline />
            </VuiBox>
            <VuiBox>
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>{conn.clientName}</VuiTypography>
              <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{conn.clientEmail}</VuiTypography>
            </VuiBox>
          </VuiBox>
          <VuiBadge
            color={cfg.color}
            size="xs"
            container
            badgeContent={cfg.label}
          />
        </VuiBox>

        <Divider sx={{ bordercolor: "#7a94b0", my: 1.5 }} />

        {/* Details grid */}
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6}>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Bank</VuiTypography>
            <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{conn.bank}</VuiTypography>
          </Grid>
          <Grid item xs={6}>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Country</VuiTypography>
            <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{conn.country}</VuiTypography>
          </Grid>
          <Grid item xs={6}>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Last Refresh</VuiTypography>
            <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{fmtDate(conn.lastRefresh)}</VuiTypography>
          </Grid>
          <Grid item xs={6}>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>Consent Expires</VuiTypography>
            <VuiTypography
              variant="caption"
              fontWeight="medium"
              sx={{ color: conn.status === "expiring" ? "#f5a623" : "white" }}
            >
              {conn.expiresAt
                ? `${fmtDate(conn.expiresAt)}${days !== null && days <= 30 ? ` (${days}d)` : ""}`
                : "—"}
            </VuiTypography>
          </Grid>
        </Grid>

        {/* Expiry warning */}
        {conn.status === "expiring" && (
          <VuiBox
            mb={1.5} px={1.5} py={1} borderRadius="8px"
            sx={{ background: "rgba(245,166,35,0.1)", border: "1px solid rgba(245,166,35,0.3)" }}
            display="flex" alignItems="center" gap={1}
          >
            <IoWarning style={{ color: "#f5a623", flexShrink: 0 }} />
            <VuiTypography variant="caption" sx={{ color: "#f5a623" }}>
              Consent expires in {days} days — renew before data stops flowing.
            </VuiTypography>
          </VuiBox>
        )}

        {/* Actions */}
        <VuiBox display="flex" gap={1} flexWrap="wrap">
          <VuiButton size="small" color="info" variant="outlined" onClick={() => onViewDetails(conn)}
            sx={{ fontSize: 11, px: 1.5, minWidth: 0, display: "flex", gap: 0.5, alignItems: "center" }}>
            <IoEyeOutline style={{ fontSize: 14 }} /> Details
          </VuiButton>
          {(conn.status === "active" || conn.status === "expiring") && (
            <VuiButton size="small" color="info" onClick={() => onRenew(conn)}
              sx={{ fontSize: 11, px: 1.5, minWidth: 0, display: "flex", gap: 0.5, alignItems: "center" }}>
              <IoRefreshOutline style={{ fontSize: 14 }} /> Renew
            </VuiButton>
          )}
          {conn.status === "pending" && (
            <VuiButton size="small" color="warning" onClick={() => onRenew(conn)}
              sx={{ fontSize: 11, px: 1.5, minWidth: 0 }}>
              Resend Link
            </VuiButton>
          )}
          {(conn.status === "active" || conn.status === "expiring") && (
            <VuiButton size="small" color="error" variant="outlined" onClick={() => onRevoke(conn)}
              sx={{ fontSize: 11, px: 1.5, minWidth: 0, display: "flex", gap: 0.5, alignItems: "center" }}>
              <IoTrashOutline style={{ fontSize: 14 }} /> Revoke
            </VuiButton>
          )}
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

// ─── Request Connection Dialog ────────────────────────────────────────────────
const INIT_FORM = { clientProfileId: "", clientName: "", clientEmail: "", country: "UK", bank: "" };

function RequestConnectionDialog({ open, onClose, tenantId }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(INIT_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  // Load active client profiles when the dialog opens
  useEffect(() => {
    if (!open || !tenantId) return;
    setProfilesLoading(true);
    listClientProfiles({ tenantId, status: "active" })
      .then(({ clientProfiles }) => setProfiles(clientProfiles))
      .catch(() => setProfiles([]))
      .finally(() => setProfilesLoading(false));
  }, [open, tenantId]);

  const banks = form.country === "UK" ? UK_BANKS : EU_BANKS;

  const handleProfileSelect = (e) => {
    const profileId = e.target.value;
    const profile = profiles.find((p) => p.id === profileId);
    setForm((prev) => ({
      ...prev,
      clientProfileId: profileId,
      // Auto-populate from the profile — accountant doesn't retype what's already recorded
      clientName:  profile?.legalName     || prev.clientName,
      clientEmail: profile?.contactEmail  || prev.clientEmail,
      country:     profile?.jurisdiction === "UK" ? "UK" : prev.country,
    }));
  };

  const handleChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleNext = () => {
    if (!form.clientProfileId) return;
    setStep(2);
  };

  const handleSend = async () => {
    setIsSending(true);
    setSendError("");
    try {
      await requestBankConsent({
        clientName:      form.clientName,
        clientEmail:     form.clientEmail,
        bank:            form.bank || null,
        country:         form.country,
        tenantId:        tenantId || null,
        clientProfileId: form.clientProfileId,
      });
      setSubmitted(true);
    } catch (err) {
      setSendError(err?.message || "Failed to send consent request. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    if (isSending) return;
    setStep(1);
    setForm(INIT_FORM);
    setSubmitted(false);
    setSendError("");
    onClose();
  };

  const selectedProfile = profiles.find((p) => p.id === form.clientProfileId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      aria-labelledby="bank-request-dialog-title"
      aria-describedby="bank-request-dialog-description"
      PaperProps={{ sx: { backgroundImage: "none", backgroundColor: "rgba(15,18,59,0.98)", color: TC.primary, border: "1px solid #d8e6f7", borderRadius: "16px" } }}>
      <DialogTitle id="bank-request-dialog-title" sx={{ color: TC.primary, pb: 0 }}>
        {submitted ? "✅ Consent Link Sent" : step === 1 ? "Request Bank Connection" : "Review & Send Consent Request"}
      </DialogTitle>

      <DialogContent id="bank-request-dialog-description" sx={{ pt: 2 }}>
        {submitted ? (
          <VuiBox textAlign="center" py={3}>
            <VuiBox mb={2} sx={{ fontSize: 56 }}>🔗</VuiBox>
            <VuiTypography variant="button" fontWeight="bold" display="block" mb={1} sx={{ color: TC.primary }}>
              Consent link sent to {form.clientEmail}
            </VuiTypography>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>
              {form.clientName} will receive a secure, single‑use link to authorise read‑only access via Open Banking.
              This consent is recorded under their client profile. You will be notified once they complete authorisation.
            </VuiTypography>
          </VuiBox>

        ) : step === 1 ? (
          <Grid container spacing={2} mt={0}>
            {/* Client Profile — the legal owner of this consent */}
            <Grid item xs={12}>
              <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>
                Client Profile * <span style={{ color: TC.secondary, fontWeight: 400 }}>— consent is owned by this profile</span>
              </VuiTypography>
              <FormControl fullWidth sx={selectSx}>
                <Select
                  value={form.clientProfileId}
                  onChange={handleProfileSelect}
                  displayEmpty
                  MenuProps={menuProps}
                >
                  <MenuItem value="" disabled>
                    <em style={{ opacity: 0.5 }}>{profilesLoading ? "Loading profiles…" : "Select a client profile"}</em>
                  </MenuItem>
                  {profiles.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.legalName}
                      <span style={{ opacity: 0.5, marginLeft: 8, fontSize: 11 }}>{p.jurisdiction} · {p.businessType}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {profiles.length === 0 && !profilesLoading && (
                <VuiTypography variant="caption" color="warning" display="block" mt={0.5}>
                  No active client profiles found. Create one in Client Profiles first.
                </VuiTypography>
              )}
            </Grid>

            {/* Profile summary strip */}
            {selectedProfile && (
              <Grid item xs={12}>
                <VuiBox px={1.5} py={1} borderRadius="8px"
                  sx={{ background: "rgba(1,181,116,0.08)", border: "1px solid rgba(1,181,116,0.2)" }}
                  display="flex" gap={2} flexWrap="wrap">
                  {[
                    ["Reg", selectedProfile.companyRegNumber || "—"],
                    ["VAT", selectedProfile.vatNumber || "—"],
                    ["Currency", selectedProfile.baseCurrency],
                    ["VAT Scheme", selectedProfile.vatScheme],
                  ].map(([k, v]) => (
                    <VuiBox key={k}>
                      <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{k}</VuiTypography>
                      <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{v}</VuiTypography>
                    </VuiBox>
                  ))}
                </VuiBox>
              </Grid>
            )}

            <Grid item xs={12}><Divider sx={{ bordercolor: "#7a94b0" }} /></Grid>

            <Grid item xs={12}>
              <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Client Contact Name *</VuiTypography>
              <VuiInput placeholder="e.g. Jane Smith" value={form.clientName} onChange={handleChange("clientName")} />
            </Grid>
            <Grid item xs={12}>
              <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Client Email *</VuiTypography>
              <VuiInput type="email" placeholder="finance@client.co.uk" value={form.clientEmail} onChange={handleChange("clientEmail")} />
            </Grid>
            <Grid item xs={6}>
              <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Country</VuiTypography>
              <FormControl fullWidth sx={selectSx}>
                <Select value={form.country} onChange={handleChange("country")} MenuProps={menuProps}>
                  <MenuItem value="UK">🇬🇧 United Kingdom</MenuItem>
                  <MenuItem value="EU">🇪🇺 European Union</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <VuiTypography variant="caption" display="block" mb={0.5} sx={{ color: TC.secondary }}>Preferred Bank (optional)</VuiTypography>
              <FormControl fullWidth sx={selectSx}>
                <Select value={form.bank} onChange={handleChange("bank")} displayEmpty MenuProps={menuProps}>
                  <MenuItem value=""><em style={{ opacity: 0.5 }}>Any bank</em></MenuItem>
                  {banks.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

        ) : (
          <VuiBox>
            {/* Summary */}
            <VuiBox p={2} mb={2} borderRadius="12px" sx={{ background: "rgba(0,117,255,0.08)", border: "1px solid rgba(0,117,255,0.2)" }}>
              <Grid container spacing={1}>
                {[
                  ["Client Profile", selectedProfile?.legalName || "—"],
                  ["Profile ID", form.clientProfileId],
                  ["Contact", form.clientName],
                  ["Email", form.clientEmail],
                  ["Country", form.country === "UK" ? "🇬🇧 UK" : "🇪🇺 EU"],
                  ["Bank", form.bank || "Client's choice"],
                ].map(([k, v]) => (
                  <Grid item xs={6} key={k}>
                    <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{k}</VuiTypography>
                    <VuiTypography variant="caption" fontWeight="medium"
                      sx={{ color: TC.primary,  wordBreak: "break-all" }}>{v}</VuiTypography>
                  </Grid>
                ))}
              </Grid>
            </VuiBox>

            {/* What client will see */}
            <VuiTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: TC.primary }}>
              What your client will be asked to authorise:
            </VuiTypography>
            {["Read account details (name, sort code, IBAN)", "Read current balances", "Read up to 90 days of transactions"].map((p) => (
              <VuiBox key={p} display="flex" alignItems="center" gap={1} mb={0.75}>
                <IoCheckmarkCircle style={{ color: "#01b574", flexShrink: 0 }} />
                <VuiTypography variant="caption" sx={{ color: TC.secondary }}>{p}</VuiTypography>
              </VuiBox>
            ))}
            {["Make payments", "Write to account", "Share credentials"].map((p) => (
              <VuiBox key={p} display="flex" alignItems="center" gap={1} mb={0.75}>
                <IoBanOutline style={{ color: "#f44336", flexShrink: 0 }} />
                <VuiTypography variant="caption" sx={{ color: TC.tertiary,  textDecoration: "line-through", opacity: 0.6 }}>{p}</VuiTypography>
              </VuiBox>
            ))}

            <VuiBox mt={2} p={1.5} borderRadius="8px"
              sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7" }}
              display="flex" gap={1}>
              <IoShieldCheckmarkOutline style={{ color: TC.linkHover, fontSize: 18, flexShrink: 0, marginTop: 2 }} />
              <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
                Consent is granted directly by the client with their bank. No credentials are shared. Access is read‑only and revocable at any time.
                This consent will be stored under the <strong style={{ color: TC.primary }}>{selectedProfile?.legalName}</strong> client profile.
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        {submitted ? (
          <VuiButton color="info" size="small" onClick={handleClose}>Done</VuiButton>
        ) : step === 1 ? (
          <>
            <VuiButton color="text" size="small" onClick={handleClose}>Cancel</VuiButton>
            <VuiButton color="info" size="small" onClick={handleNext}
              disabled={!form.clientProfileId || !form.clientName.trim() || !form.clientEmail.trim()}>
              Next →
            </VuiButton>
          </>
        ) : (
          <>
            <VuiButton color="text" size="small" onClick={() => setStep(1)} disabled={isSending}>← Back</VuiButton>
            {sendError && (
              <VuiTypography color="error" variant="caption" sx={{ flex: 1, textAlign: "left" }}>{sendError}</VuiTypography>
            )}
            <VuiButton color="info" size="small" onClick={handleSend} disabled={isSending}>
              <IoLinkOutline aria-hidden="true" style={{ marginRight: 6 }} /> {isSending ? "Sending..." : "Send Consent Link"}
            </VuiButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Consent Detail Dialog ────────────────────────────────────────────────────
function ConsentDetailDialog({ open, conn, onClose, onRevoke }) {
  if (!conn) return null;
  const cfg = STATUS_CONFIG[conn.status] || STATUS_CONFIG.expired;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      aria-labelledby="bank-connection-details-dialog-title"
      aria-describedby="bank-connection-details-dialog-description"
      PaperProps={{ sx: { backgroundImage: "none", backgroundColor: "rgba(15,18,59,0.98)", color: TC.primary, border: "1px solid #d8e6f7", borderRadius: "16px" } }}>
      <DialogTitle id="bank-connection-details-dialog-title" sx={{ color: TC.primary, pb: 0 }}>Bank Connection Details</DialogTitle>
      <DialogContent id="bank-connection-details-dialog-description" sx={{ pt: 2 }}>
        <VuiBox display="flex" alignItems="center" gap={1.5} mb={2}>
          <VuiBox display="flex" alignItems="center" justifyContent="center" width={44} height={44} borderRadius="12px"
            sx={{ background: "rgba(0,117,255,0.12)", fontSize: 22, color: TC.linkHover }}>
            <IoBusinessOutline />
          </VuiBox>
          <VuiBox>
            <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>{conn.clientName}</VuiTypography>
            <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{conn.clientEmail}</VuiTypography>
          </VuiBox>
          <VuiBox ml="auto">
            <VuiBadge color={cfg.color} size="xs" container badgeContent={cfg.label} />
          </VuiBox>
        </VuiBox>

        <Divider sx={{ bordercolor: "#7a94b0", mb: 2 }} />

        <Grid container spacing={1.5}>
          {[
            ["Bank", conn.bank],
            ["Country", conn.country],
            ["Consent ID", conn.consentId || "Not yet issued"],
            ["Granted", fmtDateTime(conn.grantedAt)],
            ["Last Refresh", fmtDateTime(conn.lastRefresh)],
            ["Expires", fmtDate(conn.expiresAt)],
          ].map(([k, v]) => (
            <Grid item xs={6} key={k}>
              <VuiTypography variant="caption" display="block" sx={{ color: TC.secondary }}>{k}</VuiTypography>
              <VuiTypography variant="caption" fontWeight="medium" sx={{ color: TC.primary }}>{v}</VuiTypography>
            </Grid>
          ))}
        </Grid>

        {conn.permissions.length > 0 && (
          <>
            <Divider sx={{ bordercolor: "#7a94b0", my: 2 }} />
            <VuiTypography variant="caption" fontWeight="bold" display="block" mb={1} sx={{ color: TC.primary }}>
              Granted Permissions
            </VuiTypography>
            <VuiBox display="flex" gap={1} flexWrap="wrap">
              {conn.permissions.map((p) => (
                <Chip key={p} label={p} size="small"
                  sx={{ backgroundColor: "rgba(1,181,116,0.12)", color: "#01b574", borderColor: "rgba(1,181,116,0.3)", border: "1px solid", fontSize: 11 }} />
              ))}
            </VuiBox>
          </>
        )}

        <VuiBox mt={2} p={1.5} borderRadius="8px" sx={{ background: "#f4f8ff", border: "1px solid #d8e6f7" }}
          display="flex" gap={1}>
          <IoInformationCircleOutline style={{ color: TC.linkHover, fontSize: 18, flexShrink: 0, marginTop: 2 }} />
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            Access is read‑only. Your client can revoke this at their bank or via this platform at any time.
            Historical data is retained as an audit trail after revocation.
          </VuiTypography>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose}>Close</VuiButton>
        {(conn.status === "active" || conn.status === "expiring") && (
          <VuiButton color="error" size="small" variant="outlined"
            onClick={() => { onRevoke(conn); onClose(); }}>
            Revoke Access
          </VuiButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Revoke Confirmation Dialog ───────────────────────────────────────────────
function RevokeDialog({ open, conn, onClose, onConfirm }) {
  if (!conn) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      aria-labelledby="bank-revoke-dialog-title"
      aria-describedby="bank-revoke-dialog-description"
      PaperProps={{ sx: { backgroundImage: "none", backgroundColor: "rgba(15,18,59,0.98)", color: TC.primary, border: "1px solid #d8e6f7", borderRadius: "16px" } }}>
      <DialogTitle id="bank-revoke-dialog-title" sx={{ color: TC.primary }}>Revoke Bank Access</DialogTitle>
      <DialogContent id="bank-revoke-dialog-description">
        <VuiTypography variant="button" sx={{ color: TC.primary }}>
          Are you sure you want to revoke Open Banking access for{" "}
          <strong style={{ color: TC.primary }}>{conn.clientName}</strong>?
        </VuiTypography>
        <VuiBox mt={1.5} p={1.5} borderRadius="8px" sx={{ background: "rgba(245,57,57,0.08)", border: "1px solid rgba(245,57,57,0.2)" }}>
          <VuiTypography variant="caption" sx={{ color: "#f86c6c" }}>
            Bank tokens will be invalidated immediately. Transaction ingestion will stop.
            Historical data is retained for audit purposes.
          </VuiTypography>
        </VuiBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <VuiButton color="text" size="small" onClick={onClose}>Cancel</VuiButton>
        <VuiButton color="error" size="small" onClick={() => { onConfirm(conn); onClose(); }}>
          Revoke Access
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const STATUS_FILTERS = ["All", "Active", "Expiring", "Pending", "Revoked", "Expired"];

export default function BankFeeds() {
  const { selectedCompanyId } = useCompany();
  const { tenantId, isSuperAdmin } = useAuthz();
  const activeTenantId = isSuperAdmin ? selectedCompanyId : tenantId;

  const [connections, setConnections] = useState(MOCK_CONNECTIONS);
  const [requestOpen, setRequestOpen] = useState(false);
  const [detailConn, setDetailConn] = useState(null);
  const [revokeConn, setRevokeConn] = useState(null);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const stats = useMemo(() => ({
    active:   connections.filter((c) => c.status === "active").length,
    expiring: connections.filter((c) => c.status === "expiring").length,
    pending:  connections.filter((c) => c.status === "pending").length,
    total:    connections.length,
  }), [connections]);

  const filtered = useMemo(() => {
    let list = connections;
    if (filter !== "All") list = list.filter((c) => c.status === filter.toLowerCase());
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => c.clientName.toLowerCase().includes(q) || c.clientEmail.toLowerCase().includes(q) || c.bank.toLowerCase().includes(q));
    }
    return list;
  }, [connections, filter, search]);

  const handleRevoke = (conn) => {
    setConnections((prev) => prev.map((c) => c.id === conn.id ? { ...c, status: "revoked", expiresAt: null } : c));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Page header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg" fontWeight="bold" sx={{ color: TC.primary }}>Bank Feeds</VuiTypography>
            <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
              Open Banking connections — read‑only, client‑authorised, PSD2 compliant
            </VuiTypography>
          </VuiBox>
          <VuiButton color="info" size="small" onClick={() => setRequestOpen(true)}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IoLinkOutline aria-hidden="true" style={{ fontSize: 16 }} /> Request Bank Connection
          </VuiButton>
        </VuiBox>

        {/* Stats */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} md={3}>
            <StatCard label="Active Connections" value={stats.active} icon={<IoCheckmarkCircle />} color="#01b574" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Expiring Soon" value={stats.expiring} icon={<IoWarning />} color="#f5a623" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Pending Consent" value={stats.pending} icon={<IoTimeOutline />} color="#0075ff" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard label="Total Connections" value={stats.total} icon={<IoCloudUploadOutline />} color="#7b61ff" />
          </Grid>
        </Grid>

        {/* Connections list */}
        <Card>
          <VuiBox p={3}>
            {/* Toolbar */}
            <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
              <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary }}>
                Client Connections
              </VuiTypography>
              <VuiBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
                {/* Status filter pills */}
                <VuiBox display="flex" gap={0.75} flexWrap="wrap">
                  {STATUS_FILTERS.map((f) => (
                    <VuiBox
                      key={f}
                      role="button"
                      tabIndex={0}
                      aria-pressed={filter === f}
                      px={1.5} py={0.5}
                      borderRadius="20px"
                      sx={{
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 600,
                        color: filter === f ? "white" : "rgba(255,255,255,0.45)",
                        background: filter === f ? "rgba(0,117,255,0.3)" : "rgba(255,255,255,0.05)",
                        border: filter === f ? "1px solid rgba(0,117,255,0.5)" : "1px solid rgba(255,255,255,0.08)",
                        transition: "all 0.2s",
                        userSelect: "none",
                      }}
                      onClick={() => setFilter(f)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setFilter(f);
                        }
                      }}
                    >
                      {f}
                    </VuiBox>
                  ))}
                </VuiBox>
                {/* Search */}
                <VuiBox sx={{ minWidth: 180 }}>
                  <VuiInput
                    placeholder="Search client or bank..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputProps={{ "aria-label": "Search bank connections" }}
                    icon={{ component: "search", direction: "left" }}
                  />
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {filtered.length === 0 ? (
              <VuiTypography variant="button" sx={{ color: TC.primary }}>No connections match your filter.</VuiTypography>
            ) : (
              <Grid container spacing={2}>
                {filtered.map((conn) => (
                  <Grid item xs={12} md={6} lg={4} key={conn.id}>
                    <ConnectionCard
                      conn={conn}
                      onViewDetails={setDetailConn}
                      onRenew={(c) => alert(`Renew consent link sent to ${c.clientEmail}`)}
                      onRevoke={setRevokeConn}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </VuiBox>
        </Card>

        {/* Compliance note */}
        <VuiBox mt={2} p={2} borderRadius="12px"
          sx={{ background: "rgba(0,117,255,0.05)", border: "1px solid rgba(0,117,255,0.15)" }}
          display="flex" alignItems="flex-start" gap={1.5}>
          <IoShieldCheckmarkOutline style={{ color: TC.linkHover, fontSize: 20, flexShrink: 0, marginTop: 2 }} />
          <VuiTypography variant="caption" sx={{ color: TC.secondary }}>
            <strong style={{ color: TC.primary }}>PSD2 / Open Banking compliance: </strong>
            Consent is always granted by the client directly with their bank. Your firm acts as a delegate only.
            Access is read‑only, scoped, and revocable at any time. No credentials or MFA secrets are stored.
            All consent events are logged for audit purposes.
          </VuiTypography>
        </VuiBox>
      </VuiBox>

      <RequestConnectionDialog open={requestOpen} onClose={() => setRequestOpen(false)} tenantId={activeTenantId} />
      <ConsentDetailDialog open={Boolean(detailConn)} conn={detailConn} onClose={() => setDetailConn(null)} onRevoke={setRevokeConn} />
      <RevokeDialog open={Boolean(revokeConn)} conn={revokeConn} onClose={() => setRevokeConn(null)} onConfirm={handleRevoke} />

      <Footer />
    </DashboardLayout>
  );
}

